import { ArrowRight } from "lucide-react-native";
import { FC, useMemo } from "react";

import { Box, Card, HStack, Pressable, Text, VStack } from "@/components/ui";

import EvidenceHover from "./EvidenceHover";
import { audienceSummary, globalFindings, nextSteps } from "./findings";
import { AUDIENCES, formatKpi, KPIS, optimize } from "./model";
import { EvidenceRequest } from "./ResponsesSheet";
import { AudienceId, Configuration, Filters, Finding } from "./types";

/* ── Types ─────────────────────────────────────── */

interface FindingsTabProps {
  audience: AudienceId;
  filterLabel: string | null;
  filters: Filters;
  onOpenEvidence: (request: EvidenceRequest) => void;
}

/* ── Constants ─────────────────────────────────── */

const BRAND_FIT = KPIS.find((k) => k.id === "brandFit")!;
const PURPLE = "#7c3aed";
const WTP = KPIS.find((k) => k.id === "wtp")!;

const TONE_CLASS: Record<Finding["tone"], string> = {
  neutral: "bg-typography-300",
  opportunity: "bg-success-500",
  risk: "bg-error-500",
};

const TONE_TEXT: Record<Finding["tone"], string> = {
  neutral: "text-typography-500",
  opportunity: "text-success-700",
  risk: "text-error-700",
};

const TONE_LABEL: Record<Finding["tone"], string> = {
  neutral: "Insight",
  opportunity: "Opportunity",
  risk: "Risk",
};

/* ── Component ─────────────────────────────────── */

const FindingsTab: FC<FindingsTabProps> = ({
  audience,
  filterLabel,
  filters,
  onOpenEvidence,
}) => {
  // Findings are read across every scenario, so evidence is shown on the
  // audience's optimum rather than on the knobs currently set.
  const optimum = useMemo(() => optimize(audience), [audience]);
  const global = useMemo(() => globalFindings(audience), [audience]);
  const steps = useMemo(() => nextSteps(), []);
  const byAudience = useMemo(
    () =>
      AUDIENCES.filter((a) => a.id !== "all").map((a) => ({
        audience: a,
        configuration: optimize(a.id).configuration,
        summary: audienceSummary(a.id, filters),
      })),
    [filters]
  );

  return (
    <VStack className="gap-4">
      {/* 1. What to test next */}
      <Card className="p-6 rounded-2xl border border-outline-200">
        <VStack className="gap-5">
          <SectionTitle
            subtitle="The decisions where two options are within a few purchase-intent points of each other: the ones where a real-consumer read adds the most before committing"
            title="Recommended for real-world testing"
          />
          <HStack className="flex-col lg:flex-row gap-3 items-stretch">
            {steps.map((step, i) => (
              <VStack
                className="flex-1 gap-4 p-4 rounded-xl border border-outline-200"
                key={i}
              >
                <VStack className="gap-1.5">
                  <Text className="text-[11px] font-medium uppercase tracking-wider text-typography-400">
                    Test {i + 1} · {audienceLabel(step.audience)}
                  </Text>
                  <Text className="text-[15px] font-semibold text-typography-900 leading-5">
                    {step.title}
                  </Text>
                </VStack>
                <VStack className="flex-1 justify-end gap-3">
                  <HStack className="items-baseline gap-1.5">
                    <Text className="text-2xl font-bold text-typography-900 tabular-nums">
                      {step.margin < 0.5 ? "<0.5" : step.margin.toFixed(1)}
                    </Text>
                    <Text className="text-xs text-typography-500">
                      {step.margin >= 0.95 && step.margin < 1.05 ? "pt" : "pts"}{" "}
                      of purchase intent between the two options
                    </Text>
                  </HStack>
                  <Text className="text-xs text-typography-500 leading-4">
                    {step.method}
                  </Text>
                </VStack>
              </VStack>
            ))}
          </HStack>
        </VStack>
      </Card>

      {/* 2. What the simulation says */}
      <Card className="p-6 rounded-2xl border border-outline-200">
        <VStack className="gap-1">
          <SectionTitle
            subtitle={`${audienceLabel(audience)}, across all ${optimum.scenarioCount.toLocaleString("en-US")} scenarios`}
            title="Key findings"
          />
          <VStack>
            {global.map((finding, i) => (
              <FindingRow
                audience={audience}
                configuration={optimum.configuration}
                finding={finding}
                first={i === 0}
                key={i}
                onOpenEvidence={onOpenEvidence}
              />
            ))}
          </VStack>
        </VStack>
      </Card>

      {/* 3. How each audience compares */}
      <Card className="p-6 rounded-2xl border border-outline-200">
        <VStack className="gap-5">
          <SectionTitle
            subtitle={`Purchase intent vs. national${
              filterLabel ? ` within ${filterLabel}` : ""
            }, and the one change each audience responds to most`}
            title="By audience"
          />
          <HStack className="flex-col lg:flex-row gap-3 items-stretch">
            {byAudience.map(({ audience: a, configuration, summary }) => (
              <VStack
                className="flex-1 gap-4 p-4 rounded-xl border border-outline-200"
                key={a.id}
              >
                <Text
                  className="text-[15px] font-semibold text-typography-900 leading-5 min-h-10"
                  numberOfLines={2}
                >
                  {a.label}
                </Text>

                <VStack className="gap-0.5">
                  <Text className="text-[11px] font-medium uppercase tracking-wider text-typography-400">
                    Purchase intent
                  </Text>
                  <Text className="text-3xl font-bold text-typography-900 tabular-nums leading-9">
                    {Math.round(summary.kpis.purchaseIntent)}%
                  </Text>
                  <Text
                    className={`text-xs font-semibold tabular-nums ${
                      Math.abs(summary.gap) < 2
                        ? "text-typography-500"
                        : summary.gap > 0
                          ? "text-success-700"
                          : "text-error-700"
                    }`}
                  >
                    {signed(summary.gap)} vs. national
                  </Text>
                </VStack>

                <VStack className="gap-1">
                  <MetaRow
                    label={WTP.label}
                    value={formatKpi(WTP, summary.kpis.wtp)}
                  />
                  <MetaRow
                    alert={summary.brandRisk}
                    label={BRAND_FIT.label}
                    value={formatKpi(BRAND_FIT, summary.kpis.brandFit)}
                  />
                </VStack>

                <VStack className="flex-1 justify-end gap-1 pt-3 border-t border-outline-200">
                  <Text className="text-[11px] font-medium uppercase tracking-wider text-typography-400">
                    Responds most to
                  </Text>
                  {summary.lever ? (
                    <VStack>
                      <Text className="text-sm font-medium text-typography-900 leading-5">
                        {summary.lever.factorLabel} →{" "}
                        {summary.lever.optionLabel}
                      </Text>
                      <Text className="text-xs text-success-700 font-semibold tabular-nums">
                        {signed(summary.lever.gain)} swing across options
                      </Text>
                    </VStack>
                  ) : (
                    <Text className="text-sm text-typography-800 leading-5">
                      No single lever stands out
                    </Text>
                  )}
                </VStack>

                <EvidenceLink
                  onPress={() =>
                    onOpenEvidence({
                      audience: a.id,
                      configuration,
                      kpi: "purchaseIntent",
                    })
                  }
                />
              </VStack>
            ))}
          </HStack>
        </VStack>
      </Card>
    </VStack>
  );
};

/* ── Subcomponents ─────────────────────────────── */

const EvidenceLink: FC<{ onPress: () => void }> = ({ onPress }) => (
  <Pressable
    className="flex-row items-center gap-1 self-start"
    onPress={onPress}
  >
    <Text className="text-xs font-medium" style={{ color: PURPLE }}>
      See all responses
    </Text>
    <ArrowRight color={PURPLE} size={12} />
  </Pressable>
);

const FindingRow: FC<{
  audience: AudienceId;
  configuration: Configuration;
  finding: Finding;
  first: boolean;
  onOpenEvidence: (request: EvidenceRequest) => void;
}> = ({ audience, configuration, finding, first, onOpenEvidence }) => (
  <HStack
    className={`gap-10 py-5 items-start ${first ? "" : "border-t border-outline-200"}`}
  >
    <VStack className="w-[34%] shrink-0 gap-1.5">
      <HStack className="items-center gap-1.5">
        <Box
          className={`w-1.5 h-1.5 rounded-full ${TONE_CLASS[finding.tone]}`}
        />
        <Text
          className={`text-[11px] font-medium uppercase tracking-wider ${TONE_TEXT[finding.tone]}`}
        >
          {TONE_LABEL[finding.tone]}
        </Text>
      </HStack>
      <EvidenceHover
        audience={audience}
        configuration={configuration}
        kpi={finding.evidenceKpi}
      >
        <Text className="text-[15px] font-semibold text-typography-900 leading-5 underline decoration-dotted decoration-outline-300 underline-offset-4">
          {finding.headline}
        </Text>
      </EvidenceHover>
    </VStack>
    <VStack className="flex-1 gap-3">
      <Text className="text-sm text-typography-700 leading-5">
        {finding.detail}
      </Text>
      {finding.quote && (
        <Text className="text-sm italic text-typography-500 leading-5 pl-3 border-l-2 border-outline-200">
          “{finding.quote.quote}”
          <Text className="text-xs not-italic text-typography-400">
            {"  "}
            {finding.quote.gender}, {finding.quote.age}, {finding.quote.state}
          </Text>
        </Text>
      )}
      <EvidenceLink
        onPress={() =>
          onOpenEvidence({ audience, configuration, kpi: finding.evidenceKpi })
        }
      />
    </VStack>
  </HStack>
);

const MetaRow: FC<{ alert?: boolean; label: string; value: string }> = ({
  alert = false,
  label,
  value,
}) => (
  <HStack className="items-baseline justify-between">
    <Text className="text-xs text-typography-500">{label}</Text>
    <Text
      className={`text-xs tabular-nums ${alert ? "font-semibold text-error-700" : "font-medium text-typography-800"}`}
    >
      {value}
    </Text>
  </HStack>
);

const SectionTitle: FC<{ subtitle: string; title: string }> = ({
  subtitle,
  title,
}) => (
  <VStack className="gap-0.5">
    <Text className="text-lg font-semibold text-typography-900">{title}</Text>
    <Text className="text-sm text-typography-500">{subtitle}</Text>
  </VStack>
);

/* ── Helpers ───────────────────────────────────── */

const audienceLabel = (audience: AudienceId): string =>
  AUDIENCES.find((a) => a.id === audience)?.label ?? "All US consumers";

const signed = (value: number): string => {
  const rounded = Math.round(value);
  if (rounded === 0) return "0 pts";
  return `${rounded > 0 ? "+" : "−"}${Math.abs(rounded)} pts`;
};

export default FindingsTab;
